import { Link as RouterLink } from "@remix-run/react";
import React, { useEffect, useState, memo } from "react";
import { Image, Button } from "@heroui/react";
import { Link as RemixLink } from "@remix-run/react";
import { FaPalette, FaBox, FaTag, FaLayerGroup } from "react-icons/fa";
import { quotesList } from "app/atoms/quote.atom";
import { useAtomValue } from "jotai";
import { RequestQuoteData } from "app/api/quote/quote.types";
import { FaFolderTree } from "react-icons/fa6";

const QuoteCard = memo(({ quote }: { quote: RequestQuoteData }) => (
  <article
    tabIndex={0}
    className="flex items-center gap-4 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <RouterLink to={quote?.productUrl} className="shrink-0">
      <Image
        width={60}
        height={60}
        loading="lazy"
        removeWrapper={true}
        src={quote?.productImage}
        alt={quote?.productName || "Product Image"}
        className="w-16 h-16 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm"
      />
    </RouterLink>
    <div className="flex flex-col gap-1">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {quote?.productName}
      </h2>
      <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
        <FaTag className="text-gray-500" size={12} /> {quote?.productCode}
      </p>
      {quote.color && (
        <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <FaPalette className="text-yellow-500" size={12} /> {quote?.color}
        </p>
      )}
      {quote.brandingMethod && (
        <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <FaLayerGroup className="text-blue-500" size={12} /> {quote?.brandingMethod} -{" "}
          {quote?.brandingPositions}
        </p>
      )}
      <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
        <FaBox className="text-green-500" size={12} /> {quote?.quantity} pcs
      </p>
    </div>
  </article>
));

const QuotePopover: React.FC<{ onToggle: (v: boolean) => void }> = ({ onToggle }) => {
  const [quotes, setQuotes] = useState<RequestQuoteData[]>([]);
  const quoteList = useAtomValue(quotesList);

  useEffect(() => {
    if (quoteList && quoteList.length > 0) {
      try {
        setQuotes(quoteList);
      } catch (error) {
        console.error("Error parsing request quotes from localStorage", error);
      }
    }
  }, [quoteList]);

  return (
    <section className="w-full min-w-96 p-5 bg-white dark:bg-gray-900 rounded-lg shadow-md">
      <h1 className="text-lg font-semibold text-center text-gray-900 dark:text-gray-100 mb-4">
        Quote Requests
      </h1>
      {quotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-24">
          <FaFolderTree size={40} className="text-gray-500" />
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
            No Quote Requests Available.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {quotes.slice(0, 3).map((quote, index) => (
            <QuoteCard key={`${quote.productId}-${index}`} quote={quote} />
          ))}
        </div>
      )}
      {quotes.length > 0 && (
        <div className="mt-4 text-center">
          <Button
            variant="light"
            as={RemixLink}
            to="/quotes"
            onPress={() => onToggle(false)}
            color="primary"
            className="font-semibold"
          >
            Review and Submit {quotes.length > 3 ? `(${quotes.length})` : ""}
          </Button>
        </div>
      )}
    </section>
  );
};

export default QuotePopover;
